import {
	Chart,
	registerables
} from 'chart.js';
import html2canvas from 'html2canvas';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Badge, Breadcrumb, Button, OverlayTrigger, Tab, Table, Tabs } from 'react-bootstrap';
import { Scatter } from "react-chartjs-2";
import { withTranslation } from 'react-i18next';
import { BiComment, BiListCheck, BiPencil, BiSort } from "react-icons/bi";
import { BsChevronLeft, BsChevronRight, BsInfoCircle, BsQuestionCircle, BsXSquare } from "react-icons/bs";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from "react-toastify";
import { LoaderContext } from '../../../../App';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import { LS_AUTHORITY_KEY, LS_COMPANY_INFO_KEY, MaterialityImpactRange, MaterialityImpactType, MaterialityType, SORT_ORDER_ASC, SORT_ORDER_DESC } from '../../../../core/constants/common';
import MaterialIssueService from '../../../../core/service/MaterialIssueService';
import ReportService from '../../../../core/service/ReportService';
import Utils from '../../../../core/utilities/Utils';
import MaterialityImpactSelectionModal from './Issues/MaterialityImpactSelectionModal';
import './MaterialityAssessment.scss';
import QuestionScoreModal from './QuestionScoreModal';

Chart.register(...registerables);

const SortBy = {
	STAKEHOLDER_RANKING: 0,
	FINANCIAL_WEIGHTED_SCORE: 1,
	IMPACT_WEIGHTED_SCORE: 2,
	COMPANY_RANKING: 3
};
const customLinesPlugin = {
	id: 'customLinesPlugin',
	beforeDatasetsDraw(chart, args, options) {
		const { ctx, chartArea, scales } = chart;
		const xScale = scales.x;
		const yScale = scales.y;

		if (!xScale || !yScale || !chartArea) return;

		// Get active tooltip position
		const tooltip = chart.tooltip;
		const activePoint = tooltip?.getActiveElements()?.[0];
		let activeY = null;
		if (activePoint) {
			activeY = activePoint.element.y;
		}

		const drawLine = (x1, y1, x2, y2, color, lineWidth = 2) => {

			ctx.save();
			// Reduce opacity if line is near the active tooltip
			if (activeY && Math.abs(y1 - activeY) < 20) {
				ctx.strokeStyle = color.replace(')', ', 0.2)'); // Reduce opacity to 0.2
			} else {
				ctx.strokeStyle = color;
			}
			ctx.lineWidth = lineWidth;
			ctx.beginPath();
			ctx.moveTo(x1, y1);
			ctx.lineTo(x2, y2);
			ctx.stroke();
			ctx.restore();
		};

		const drawArrow = (x1, y1, x2, y2, color, lineWidth = 2) => {
			ctx.save();
			ctx.strokeStyle = color;
			ctx.fillStyle = color;
			ctx.lineWidth = lineWidth;
			ctx.beginPath();
			ctx.moveTo(x1, y1);
			ctx.lineTo(x2, y2);
			ctx.stroke();

			const arrowSize = 14;
			let angle = Math.atan2(y2 - y1, x2 - x1);

			ctx.beginPath();
			ctx.moveTo(x2, y2);
			ctx.lineTo(
				x2 - arrowSize * Math.cos(angle - Math.PI / 6),
				y2 - arrowSize * Math.sin(angle - Math.PI / 6)
			);
			ctx.lineTo(
				x2 - arrowSize * Math.cos(angle + Math.PI / 6),
				y2 - arrowSize * Math.sin(angle + Math.PI / 6)
			);
			ctx.closePath();
			ctx.fill();
			ctx.restore();
		};

		// Pixel values for lines
		const y1 = yScale.getPixelForValue(1);
		const y2 = yScale.getPixelForValue(2);
		const y3 = yScale.getPixelForValue(3);
		const y4 = yScale.getPixelForValue(4);

		const x1 = xScale.getPixelForValue(1);
		const x2 = xScale.getPixelForValue(2);
		const x3 = xScale.getPixelForValue(3);
		const x4 = xScale.getPixelForValue(4);

		const yBottom = yScale.getPixelForValue(0);
		const xLeft = xScale.getPixelForValue(0);

		// Horizontal + vertical lines (gray, orange, green, red)
		drawLine(xLeft, y1, x1, y1, 'rgb(82, 80, 78,0.8)');
		drawLine(x1, yBottom, x1, y1, 'rgb(82, 80, 78,0.8)');

		drawLine(xLeft, y2, x2, y2, 'rgba(0, 128, 0, 0.8)');
		drawLine(x2, yBottom, x2, y2, 'rgba(0, 128, 0, 0.8)');

		drawLine(xLeft, y3, x3, y3, 'rgba(255, 165, 0, 0.8)');
		drawLine(x3, yBottom, x3, y3, 'rgba(255, 165, 0, 0.8)');


		drawLine(xLeft, y4, x4, y4, 'rgba(255, 0, 0, 0.8)');
		drawLine(x4, yBottom, x4, y4, 'rgba(255, 0, 0, 0.8)');

		// Bold X and Y axes
		drawLine(xLeft - 2, yBottom, chartArea.right, yBottom, 'rgb(82, 80, 78)', 5);
		drawLine(xLeft, yBottom, xLeft, chartArea.top, 'rgb(82, 80, 78)', 5);

		// Arrows
		drawArrow(chartArea.right - 10, yBottom, chartArea.right + 5, yBottom, '#52504E');
		drawArrow(xLeft, yBottom, xLeft, chartArea.top - 10, '#52504E');
	}
};

const ScoreCard = ({ t }) => {
	const lsDat = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyId = lsDat.profile.company.id;
	const companyName = lsDat.profile.company.legalName;
	const lsCompanyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY));

	const { setLoaderDisplay } = useContext(LoaderContext);
	const [scores, setScores] = useState([]);
	const [impactStakeholders, setImpactStakeholders] = useState([]);
	const [financialStakeholders, setFinancialStakeholders] = useState([]);
	const [searchParams] = useSearchParams();
	const assessmentId = searchParams.get('aId') ?? 0;
	const version = searchParams.get('version') ?? '';
	const status = searchParams.get('status') ?? 'DRAFT';
	const mode = searchParams.get('mode') ?? 'VIEW';
	const [isRankEdit, setIsRankEdit] = useState(false);
	const navigate = useNavigate();
	const [selectedItems, setSelectedItems] = useState([]);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
	const [saveChanges, setSaveChanges] = useState(false);
	const [chartConfig, setChartConfig] = useState({});
	const [chartConfigrp, setChartConfigrp] = useState({});
	const [activeTab, setActiveTab] = useState('scorecard');
	const location = useLocation();
	const [sortOrder, setSortOrder] = useState(SORT_ORDER_DESC);
	const [sortField, setSortField] = useState(SortBy.STAKEHOLDER_RANKING);
	const [selectedComment, setSelectedComment] = useState(null);
	const [selectedGroup, setSelectedGroup] = useState(null);
	const exportRef = useRef(null);
	const [showProceedToReportConfirmation, setShowProceedToReportConfirmation] = useState(false);
	const [showSaveModal, setShowSaveModal] = useState(false);
	const [importanceStakeholders, setImportanceStakeholders] = useState([]);
	const [matIssueVersionType, setMatIssueVersionType] = useState(lsCompanyInfo?.materialityType ?? MaterialityType.DOUBLE);	
	const colorMap = {
		ENVIRONMENTAL: "#2dc26b",
		SOCIAL: "#007bff",
		GOVERNANCE: "#f7c300"
	};
	const quadrantColors = [
		{ color: '#52504E', label: 'Low' }, // Bottom-left (green)
		{ color: '#008000', label: 'Medium' }, // Bottom-right (light blue)
		{ color: '#FFA500', label: 'High' }, // Top-left (orange)
		{ color: '#FF0000', label: 'Very High' }, // Top-right (red)
	];

	useEffect(() => {
		getScorecard(sortOrder, sortField);
	}, [sortOrder, sortField]);

	const getScorecard = (sOrder, sField) => {
		setLoaderDisplay(true);
		MaterialIssueService.getScoreCard(assessmentId, version).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			setupScoreCardData(res.data.data, sOrder, sField);
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const saveScorecard = () => {
		if (scores.some(score => score.active && !score.rankingByCompany)) {
			toast.warning("Please provide the rankings for all the selected issues.", {
				position: toast.POSITION.TOP_RIGHT,
			});

			return;
		}

		const dataToSend = [];
		scores.forEach(element => {
			dataToSend.push({
				questionId: element.questionId,
				ranking: element.rankingByCompany,
				active: element.active,
			});
		});
		setLoaderDisplay(true);
		MaterialIssueService.saveScoreCardDet(assessmentId, version, companyId, { rankings: dataToSend }).then(res => {
			setLoaderDisplay(false);
			setShowSaveModal(true);
			setIsRankEdit(false);
			setSaveChanges(false);
			getScorecard();
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const handleRankingValidation = (value, index) => {
		if (scores.some((score, idx) => score.rankingByCompany === value && idx !== index)) {
			toast.warning("Rankings must be unique. Please enter a different value.", {
				position: toast.POSITION.TOP_RIGHT,
			});

			let data = [...scores];
			data[index].rankingByCompany = '';
			setScores(data);
			return;
		}
	}

	const patchRanking = (value, index) => {
		let data = [...scores];
		data[index].rankingByCompany = value;
		setScores(data);
		setSaveChanges(true);
	}

	const onPageSelection = (id) => {
		let data = [...scores];
		const ids = [...selectedItems];
		const index = selectedItems.indexOf(id);
		const activeindex = data.findIndex(x => x.questionId === id);
		if (index >= 0) {
			ids.splice(index, 1);
			data[activeindex].active = false;
		} else {
			ids.push(id);
			data[activeindex].active = true;
		}
		setSelectedItems(ids);
		setScores(data);
		setSaveChanges(true);
	};

	const onSelectAllPages = () => {
		let data = [...scores];
		if (scores.length === selectedItems.length) {
			data?.forEach(el => {
				el.active = false;
			});
			setSelectedItems([]);
			setScores(data);
			setSaveChanges(true);
		} else {
			const ids = [];
			data?.forEach(el => {
				el.active = true;
				ids.push(el.questionId);
			});
			setSelectedItems(ids);
			setScores(data);
			setSaveChanges(true);
		}
	};

	const confirmFinalize = (val) => {
		setShowConfirmation(false);
		if (val) {
			if (selectedItems.length > 0) {
				exportChartAndFinalizeScorecard();
			} else {
				toast.warning("For finalizing, atleast one item needs to be selected", {
					position: toast.POSITION.TOP_RIGHT,
				});
			}
		}
	};

	const saveChangesConfirmationCallBack = (val) => {
		setShowCloseConfirmation(false);
		setIsRankEdit(false);
		if (val) {
			saveScorecard();
		}
	}

	const navigateToReport = (val) => {
		setShowProceedToReportConfirmation(false);
		if (val) {
			navigate('/company_admin/dmat/report/list');
		} else {
			gotoList();
		}
	}

	const gotoList = () => {
		navigate('/company_admin/dmat/scorecard/list');
	}

	const transformAssessmentName = () => {
		const parts = location.state?.assessmentName.split(" V");
		const baseName = parts[0].trim();
		const version = parts[1]?.split(" ")[0];

		return (
			<>
				{baseName}
				{version && <Badge pill bg='info' className='ms-2'>Version {version}</Badge>}
			</>
		);
	}

	const renderHeader = () => {
		return (
			<>
				<div className="List_header pb-0">
					<Breadcrumb className='h5'>
						<Breadcrumb.Item onClick={() => navigate({ pathname: "/company_admin/dmat/stats" })}>{t("Materiality.label")}</Breadcrumb.Item>
						<Breadcrumb.Item onClick={() => navigate({ pathname: `/company_admin/dmat/scorecard/list?aId=${assessmentId}` })}>{t("ScoreCards.label")}</Breadcrumb.Item>
						<Breadcrumb.Item active>{transformAssessmentName()}</Breadcrumb.Item>
					</Breadcrumb>
					<div className="header-actions">
						{/* <button className="btn btn-headbtn" >
							<BsFilter />
						</button>
						<button className="btn btn-headbtn" >
							<BiDownload />
						</button> */}
						<span className="badge bg-badge-Accepted rounded-pill">{matIssueVersionType == 'SINGLE' ? 'Impact Materiality' : 'Double Materiality'}</span>
						<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t('PreviousVersions.label'))}>
							<Button variant='primary' className='ListCheckBtn' onClick={() => gotoList()}>
								<BiListCheck />
							</Button>
						</OverlayTrigger>

						<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("NavigateBackAndNext.MatAssessment"))}>
							<Button variant='secondary' className='back-btn' onClick={() => navigate(`/company_admin/dmat/matassessments`)}>
								<BsChevronLeft /> <span>{t("Back.label")}</span>
							</Button>
						</OverlayTrigger>
						<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t("NavigateBackAndNext.MatReport"))}>
							<Button variant='secondary' className='next-btn' onClick={() => navigate(`/company_admin/dmat/report/list`)}>
								<span>{t("Next.label")}</span> <BsChevronRight />
							</Button>
						</OverlayTrigger>
					</div>
				</div>
				<div>
					{t("ScoreCardTitleText.label")}
				</div>
			</>
		);
	};

	const renderTableHeader = () => {
		return (
			<thead>
				<tr>
					<th rowSpan={2} className='First_child' width={'4%'}>
						<input className="form-check-input" type="checkbox" id={`selectAllpageCheckbox`}
							checked={selectedItems?.length === scores.length}
							onChange={() => onSelectAllPages()} disabled={mode == 'VIEW'} />
						<OverlayTrigger
							placement="top"
							overlay={Utils.renderTooltip(
								t('CheckBoxMsg.label')
							)}>
							<span className='ms-1'>
								<BsInfoCircle />
							</span>

						</OverlayTrigger>
					</th>
					<th rowSpan={2} className='second_child bordeRright02'>{t('MaterialIssue.label')}</th>
					<th rowSpan={2} className='text-center bordeRright02'>
						<span className='cursor' onClick={() => handleSort(SortBy.STAKEHOLDER_RANKING)}>
							{t('StakeholderRanking.label')}
							<BiSort />
						</span>
						<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t('SHRankingTooltip.label'))}>
							<span className='ms-1'><BsInfoCircle /></span>
						</OverlayTrigger>
					</th>
					<th colSpan={impactStakeholders?.length + 1} className='text-center LightBLue'>
						{/* {t('ImpactMateriality.label')} (0-4) */}
						{matIssueVersionType === MaterialityType.SINGLE ? `${t('ImpactMateriality.label')} (0-4)` : 'Impact Materiality (0-4)'}
						<OverlayTrigger
							placement="top"
							overlay={Utils.renderTooltip(
								t('ImpactMaterialityTooltip.label')
							)}>
							<span className='ms-1'>
								<BsInfoCircle />
							</span>
						</OverlayTrigger>
					</th>
					{ 					                    
						//  #30263 - financialStakeholders.length can be 0 as financial questions can be trurned off at stakeholderlevel,
						// therefore always rely on impactStakeholders.length to  ensure that NA is shown for all non-existising financialStakeholders groupscolumns.
						matIssueVersionType === MaterialityType.DOUBLE && 
						<th colSpan={impactStakeholders?.length + 1} className='text-center'>
							{t('FinancialMateriality.label')} (0-4)
							<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t('FinancialMaterialityTooltip.label'))}>
								<span className='ms-1'><BsInfoCircle /></span>
							</OverlayTrigger>
						</th>
					}
					{
						matIssueVersionType === MaterialityType.SINGLE && 
						<th colSpan={importanceStakeholders?.length + 1} className='text-center'>
							{t('ImportanceMateriality.label')} (0-4)
							<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t('ImportanceMaterialityTooltip.label'))}>
								<span className='ms-1'><BsInfoCircle /></span>
							</OverlayTrigger>
						</th>
					}
					{/* <th rowSpan={2} className='text-center'>Financial(F)/Impact(I)</th>
					<th rowSpan={2} className='text-center'>Risk(R)/Oppurtunity(O)</th> */}
					<th colSpan='3' className='text-center LightBLue'>
						{t('CompanyEvaluation.label')}
						<OverlayTrigger
							placement="top"
							overlay={Utils.renderTooltip(
								t('CompanyEvalHelper.label')
							)}>
							<span className='ms-1'>
								<BsInfoCircle />
							</span>
						</OverlayTrigger>
					</th>
					<th rowSpan={2} className='text-center'>
						<span className='cursor' onClick={() => handleSort(SortBy.COMPANY_RANKING)}>
							{t('RankingByCompany.label')}
							<BiSort />
						</span>
						<OverlayTrigger placement="top" overlay={Utils.renderTooltip(t('RankingByCompTooltip.label'))}>
							<span className='ms-1'><BsInfoCircle /></span>
						</OverlayTrigger>
						<span className='ms-1'>
							{
								mode == 'EDIT' && (
									<>
										{
											isRankEdit
												?
												<OverlayTrigger
													placement="left"
													overlay={Utils.renderTooltip(t("CancelEdit.label"))}>
													<span>
														<BsXSquare
															className="cursor" onClick={() => setShowCloseConfirmation(true)} />
													</span>
												</OverlayTrigger>
												:
												<OverlayTrigger
													placement="left"
													overlay={Utils.renderTooltip(t("Edit.label"))}>
													<span>
														<BiPencil
															className="cursor" onClick={() => setIsRankEdit(true)} />
													</span>
												</OverlayTrigger>
										}
									</>
								)
							}
						</span>
					</th>
				</tr>
				<tr>
					<th className='text-center LightBLue' key='scoreHeaderImpWS'>
						<span className='cursor' onClick={() => handleSort(SortBy.IMPACT_WEIGHTED_SCORE)}>
							{t('WeightedScore.label')}
							<BiSort />
						</span>
					</th>
					{
						impactStakeholders?.map((s, index) => {
							return (
								<th className="text-center LightBLue" key={`scoreHeader-${index}`}>{s.stakeHolderName}</th>
							);
						})
					}
					<th className='text-center' key='scoreHeaderFinWS'>
						<span className='cursor' onClick={() => handleSort(SortBy.FINANCIAL_WEIGHTED_SCORE)}>
							{t('WeightedScore.label')}
							<BiSort />
						</span>
					</th>					
					{
				                       
                    	//  #30263 - financialStakeholders.length can be 0 as financial questions can be trurned off at stakeholderlevel,
						// therefore always rely on impactStakeholders.length to  ensure that NA is shown for all non-existising financialStakeholders groupscolumns.
						matIssueVersionType === MaterialityType.DOUBLE && 
						impactStakeholders?.map((s, index) => {
							return (
								<th className="text-center" key={`scoreHeader-${index}`}>{s.stakeHolderName}</th>
							);
						})
					}
					{
						matIssueVersionType === MaterialityType.SINGLE && 
						importanceStakeholders?.map((s, index) => {
							return (
								<th className="text-center" key={`scoreHeader-${index}`}>{s.stakeHolderName}</th>
							);
						})
					}
					<th className='text-center LightBLue' key='compEvalImp'>
						{t('Impact.label')}
					</th>
					{
						matIssueVersionType === MaterialityType.DOUBLE &&
						<th className='text-center LightBLue' key='compEvalFin'>
							{t('Financial.label')}
						</th>
					}
					{
						matIssueVersionType === MaterialityType.SINGLE &&
						<th className='text-center LightBLue' key='compEvalFin'>
							{t('Importance.label')}
						</th>
					}
					<th className='text-center LightBLue' key='compEvalComments'>
						{t('Comments.label')}
					</th>
				</tr>

			</thead>
		);
	};

	const renderRows = () => {
		return (
			scores?.map((score, index) => {
				return (
					<tr key={`scoreRow-${index}`}>
						<td className='First_child'>
							<input className="form-check-input" type="checkbox" id={`scoreCheckbox-${index}`}
								checked={selectedItems.indexOf(score.questionId) >= 0}
								onChange={() => onPageSelection(score.questionId)} disabled={mode == 'VIEW'} />
						</td>
						<td className='second_child'>
							<div className='materialIssue_dt'>
								<p className='m-0'>{score.topic?.name}</p>
								{
									score.topic?.description &&
									<OverlayTrigger
										placement="left"
										overlay={Utils.renderTooltip(score.topic?.description)}>
										<span><BsQuestionCircle /></span>
									</OverlayTrigger>
								}
							</div>
						</td>
						<td className='text-center'>{score.stakeholderRanking}</td>
						<td className='text-center LightBLue'>{score.impact.averageScore}</td>
						{
							impactStakeholders?.map((s, sIndex) => {
								const scoreData = score.impact?.stakeholderGroups?.find(holder => holder.stakeholderGroupId === s.stakeholderGroupId);
								return (
									<td className="text-center LightBLue" key={`scoreStakeHolderImp-${index}-${sIndex}`}>
										{
											scoreData
												?
												<span className="levelCard 2"
													onClick={() => setSelectedGroup({ score: score, groupId: scoreData.stakeholderGroupId })}>
													{scoreData?.averageScore}
												</span>
												: <span>NA</span>
										}
									</td>
								);
							})
						}
						{matIssueVersionType === MaterialityType.DOUBLE && <td className='text-center'>{score.financial.averageScore || 'NA'}</td>}
						{matIssueVersionType === MaterialityType.SINGLE && <td className='text-center'>{score.importance.averageScore}</td>}
						{
							 //  #30263 - financialStakeholders.length can be 0 as financial questions can be trurned off at stakeholderlevel,
						    // therefore always rely on impactStakeholders.length to  ensure that NA is shown for all non-existising financialStakeholders groupscolumns.
							(matIssueVersionType === MaterialityType.DOUBLE
								? impactStakeholders : matIssueVersionType === MaterialityType.SINGLE
								? importanceStakeholders : [])?.map((s, sIndex) => {
								let scoreData;
								if (matIssueVersionType === MaterialityType.SINGLE ) {
									scoreData = score.importance?.stakeholderGroups?.find(holder => holder.stakeholderGroupId === s.stakeholderGroupId);
								} else {
									scoreData = score.financial?.stakeholderGroups?.find(holder => holder.stakeholderGroupId === s.stakeholderGroupId);
								} 
								return (
									<td className='text-center' key={`scoreStakeHolderFin-${index}-${sIndex}`}>
										{
											scoreData
												?
												<span className="levelCard 1"
													onClick={() => setSelectedGroup({ score: score, groupId: scoreData.stakeholderGroupId })}>
													{scoreData?.averageScore}
												</span>
												: <span>NA</span>
										}
									</td>
								);
							})
						}
						{renderFinAndImpactOriginalScores(score)}
						<td className='text-center'>
							{
								isRankEdit
									?
									<input type='text' className='form-control ' value={score?.rankingByCompany ? score?.rankingByCompany : ''}
										onChange={(e) => patchRanking(+e.target.value, index)}
										onBlur={(e) => handleRankingValidation(+e.target.value, index)} />
									:
									<label>{score?.rankingByCompany}</label>
							}
						</td>
					</tr>
				);
			})
		);
	};

	const getBubbleColor = (x, y) => {
		const maxScore = Math.max(x, y); // Compare financial and impact scores
		if (maxScore >= 3) return '#FF0000'; // Red
		if (maxScore >= 2) return '#FFA500'; // Orange
		if (maxScore >= 1) return '#008000'; // Green
		return '#808080'; // Gray (Default for scores < 2)
	};

	const setupChartData = (data, isSingleMateriality) => {
		const chartData = data.map((item, index) => {
			const xAvgScore = isSingleMateriality ? item.importance.averageScore : item.financial.averageScore;
			return {
				x: xAvgScore,
				y: item.impact.averageScore,
				label: index + 1,
				rank: index + 1,
				type: item.topic?.category,
				name: item.topic?.name,
				color: getBubbleColor(xAvgScore, item.impact.averageScore),
			};
		});
		const chartDatarp = data.map((item, index) => {
			const xAvgScore = isSingleMateriality ? item.importance.averageScore : item.financial.averageScore;
			return {
				x: xAvgScore,
				y: item.impact.averageScore,
				label: index + 1,

				rank: index + 1,
				type: item.topic?.category,
				name: item.topic?.name,
				color: getBubbleColor(xAvgScore, item.impact.averageScore),
			};
		});

		setChartConfig({
			type: "scatter",
			data: {
				datasets: [{
					label: "Materiality Matrix",
					data: chartData,
					//backgroundColor: chartData.map(point => point.color),
					backgroundColor: chartData.map(point => colorMap[point.type]),
					pointRadius: 14,
					borderColor: '#fff',
					borderWidth: 2,
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					x: {
						title: { 
							display: true, 
							text: t(isSingleMateriality ? 'ImportanceMateriality.label' : 'FinancialMateriality.label').toUpperCase(), 
							font: { size: 16, family: 'sans-serif' }, color: 'rgb(82, 80, 78)' 
						},
						min: 0,
						max: 4.3,
						ticks: {
							stepSize: 1,
							font: { size: 17 },
							weight: 'bold', // Make it bold like the image
							padding: 7,
							callback: (value) => value >= 0 && value <= 4 ? value : ''
						},
						grid: {
							color: 'rgb(255, 255, 255,0.4)',
							lineWidth: 1,
							drawTicks: false,
						},
					},
					y: {
						title: { display: true, text: t('ImpactMateriality.label').toUpperCase(), font: { size: 16, family: 'sans-serif' }, color: 'rgb(82, 80, 78)', weight: 'bold' },
						min: 0,
						max: 4.3,
						ticks: {
							stepSize: 1,
							font: { size: 17 },
							weight: 'bold',
							padding: 7,
							callback: (value) => value >= 0 && value <= 4 ? value : ''
						},
						grid: {
							color: 'rgb(255, 255, 255)',
							lineWidth: 1,
							drawTicks: false
						},
					}
				},
				plugins: {
					legend: { display: false },
					tooltip: {
						enabled: false,  // Disable the default tooltip
						external: function (context) {
							// Tooltip Element
							let tooltipEl = document.getElementById('chartjs-tooltip');

							// Create element on first render
							if (!tooltipEl) {
								tooltipEl = document.createElement('div');
								tooltipEl.id = 'chartjs-tooltip';
								tooltipEl.style.background = 'rgba(9, 9, 9, 0.9)';
								tooltipEl.style.borderRadius = '5px';
								tooltipEl.style.color = '#fff';
								tooltipEl.style.opacity = 1;
								tooltipEl.style.pointerEvents = 'none';
								tooltipEl.style.position = 'absolute';
								tooltipEl.style.transform = 'translate(-50%, 0)';
								tooltipEl.style.transition = 'all .1s ease';
								tooltipEl.style.boxShadow = '0 0 0 3px white'; // Add white border
								tooltipEl.style.zIndex = 999999;

								const table = document.createElement('table');
								table.style.margin = '0px';

								tooltipEl.appendChild(table);
								document.body.appendChild(tooltipEl);
							}

							// Hide if no tooltip
							const tooltipModel = context.tooltip;
							if (tooltipModel.opacity === 0) {
								tooltipEl.style.opacity = 0;
								return;
							}

							// Set Text
							if (tooltipModel.body) {
								const titleLines = tooltipModel.title || [];
								const bodyLines = tooltipModel.body.map(b => b.lines);

								const tableBody = document.createElement('tbody');

								titleLines.forEach(title => {
									const tr = document.createElement('tr');
									const td = document.createElement('td');
									td.style.borderWidth = 0;
									td.style.padding = '2px 5px';
									td.style.fontSize = '12px';

									const text = document.createTextNode(title);
									td.appendChild(text);
									tr.appendChild(td);
									tableBody.appendChild(tr);
								});

								bodyLines[0].forEach(line => {
									const tr = document.createElement('tr');
									const td = document.createElement('td');
									td.style.borderWidth = 0;
									td.style.padding = '2px 5px';
									td.style.fontSize = '12px';
									td.style.whiteSpace = 'nowrap';

									const text = document.createTextNode(line);
									td.appendChild(text);
									tr.appendChild(td);
									tableBody.appendChild(tr);
								});

								const tableRoot = tooltipEl.querySelector('table');
								// Remove old children
								while (tableRoot.firstChild) {
									tableRoot.firstChild.remove();
								}
								// Add new children
								tableRoot.appendChild(tableBody);
							}

							const position = context.chart.canvas.getBoundingClientRect();
							const bodyFont = context.chart.options.font;

							// Display, position, and set styles for font
							tooltipEl.style.opacity = 1;
							tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
							tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
							tooltipEl.style.font = bodyFont.string;
							tooltipEl.style.padding = tooltipModel.padding + 'px ' + tooltipModel.padding + 'px';
						},
						callbacks: {
							label: (context) => {
								const data = context.dataset.data[context.dataIndex];
								return [
									`Stakeholder Ranking: ${data.rank}`,
									`Category: ${data.type}`,
									`Topic: ${data.name}`,
									`${isSingleMateriality ? 'Importance' : 'Financial'}: ${context.parsed.x.toFixed(2)}`,
									`Impact: ${context.parsed.y.toFixed(2)}`
								];
							}
						}
					},
					datalabels: {
						color: '#fff',
						font: { size: 11, weight: 'bold' },
						align: 'center',
						anchor: 'center',
						offset: 0,
						formatter: (value) => value.label,
					},
					customLinesPlugin: {}
				},
			},
		});
		setChartConfigrp({
			type: "scatter",
			data: {
				datasets: [{
					label: isSingleMateriality ? "Impact Materiality Matrix" : "Double Materiality Matrix",
					data: chartDatarp,
					//backgroundColor: chartData.map(point => point.color),
					backgroundColor: chartData.map(point => colorMap[point.type]),
					pointRadius: 12,
					borderColor: '#fff',
					borderWidth: 2,
				}]
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					x: {
						title: { display: true, 
							text: t(isSingleMateriality ? 'ImportanceMateriality.label' : 'FinancialMateriality.label').toUpperCase(), 
							font: { size: 16, family: 'sans-serif' }, color: 'rgb(82, 80, 78)' },
						min: 0,
						max: 4,
						ticks: {
							stepSize: 1,
							font: { size: 17 },
							weight: 'bold', // Make it bold like the image
							padding: 7,
							callback: (value) => value >= 0 && value <= 4 ? value : ''
						},
						grid: {
							color: 'rgb(255, 255, 255,0.4)',
							lineWidth: 1,
							drawTicks: false,
						},
					},
					y: {
						title: { display: true, text: t('ImpactMateriality.label').toUpperCase(), font: { size: 16, family: 'sans-serif' }, color: 'rgb(82, 80, 78)', weight: 'bold' },
						min: 0,
						max: 4,
						ticks: {
							stepSize: 1,
							font: { size: 17 },
							weight: 'bold',
							padding: 7,
							callback: (value) => value >= 0 && value <= 4 ? value : ''
						},
						grid: {
							color: 'rgb(255, 255, 255)',
							lineWidth: 1,
							drawTicks: false
						},
					}
				},
				plugins: {
					legend: { display: false },
					tooltip: {
						enabled: false,  // Disable the default tooltip
						external: function (context) {
							// Tooltip Element
							let tooltipEl = document.getElementById('chartjs-tooltip');

							// Create element on first render
							if (!tooltipEl) {
								tooltipEl = document.createElement('div');
								tooltipEl.id = 'chartjs-tooltip';
								tooltipEl.style.background = 'rgba(9, 9, 9, 0.9)';
								tooltipEl.style.borderRadius = '5px';
								tooltipEl.style.color = '#fff';
								tooltipEl.style.opacity = 1;
								tooltipEl.style.pointerEvents = 'none';
								tooltipEl.style.position = 'absolute';
								tooltipEl.style.transform = 'translate(-50%, 0)';
								tooltipEl.style.transition = 'all .1s ease';
								tooltipEl.style.boxShadow = '0 0 0 3px white'; // Add white border
								tooltipEl.style.zIndex = 999999;

								const table = document.createElement('table');
								table.style.margin = '0px';

								tooltipEl.appendChild(table);
								document.body.appendChild(tooltipEl);
							}

							// Hide if no tooltip
							const tooltipModel = context.tooltip;
							if (tooltipModel.opacity === 0) {
								tooltipEl.style.opacity = 0;
								return;
							}

							// Set Text
							if (tooltipModel.body) {
								const titleLines = tooltipModel.title || [];
								const bodyLines = tooltipModel.body.map(b => b.lines);

								const tableBody = document.createElement('tbody');

								titleLines.forEach(title => {
									const tr = document.createElement('tr');
									const td = document.createElement('td');
									td.style.borderWidth = 0;
									td.style.padding = '2px 5px';
									td.style.fontSize = '12px';

									const text = document.createTextNode(title);
									td.appendChild(text);
									tr.appendChild(td);
									tableBody.appendChild(tr);
								});

								bodyLines[0].forEach(line => {
									const tr = document.createElement('tr');
									const td = document.createElement('td');
									td.style.borderWidth = 0;
									td.style.padding = '2px 5px';
									td.style.fontSize = '12px';
									td.style.whiteSpace = 'nowrap';

									const text = document.createTextNode(line);
									td.appendChild(text);
									tr.appendChild(td);
									tableBody.appendChild(tr);
								});

								const tableRoot = tooltipEl.querySelector('table');
								// Remove old children
								while (tableRoot.firstChild) {
									tableRoot.firstChild.remove();
								}
								// Add new children
								tableRoot.appendChild(tableBody);
							}

							const position = context.chart.canvas.getBoundingClientRect();
							const bodyFont = context.chart.options.font;

							// Display, position, and set styles for font
							tooltipEl.style.opacity = 1;
							tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
							tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
							tooltipEl.style.font = bodyFont.string;
							tooltipEl.style.padding = tooltipModel.padding + 'px ' + tooltipModel.padding + 'px';
						},
						callbacks: {
							label: (context) => {
								const data = context.dataset.data[context.dataIndex];
								return [
									`Stakeholder Ranking: ${data.rank}`,
									`Category: ${data.type}`,
									`Topic: ${data.name}`,
									`${isSingleMateriality ? 'Importance' : 'Financial'}: ${context.parsed.x.toFixed(2)}`,
									`Impact: ${context.parsed.y.toFixed(2)}`
								];
							}
						}
					},
					datalabels: {
						color: '#fff',
						font: { size: 11, weight: 'bold' },
						align: 'center',
						anchor: 'center',
						offset: 0,
						formatter: (value) => value.label,
					},
					customLinesPlugin: {}
				},
			},
		});
	};

	const exportChartAndFinalizeScorecard = async () => {
		if (!exportRef.current) return;

		setLoaderDisplay(true);
		const canvas = await html2canvas(exportRef.current, { scale: 2, useCORS: true });
		const imageBlob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
		if (!imageBlob) {
			toast.warning("Failed to export materiality matrix", {
				position: toast.POSITION.TOP_RIGHT,
			});
			setLoaderDisplay(false);
			finalizeScorecard();
			return;
		}
		const formData = new FormData();
		formData.append("file", imageBlob, "matrix_data.png");
		ReportService.uploadImage(formData)
			.then((response) => {
				const imgRes = response.data;
				const graphImageURL = ReportService.getImage(imgRes.reference);
				finalizeScorecard(graphImageURL);
			})
			.catch((error) => {
				finalizeScorecard();
			})
			.finally(() => {
				setLoaderDisplay(false);
			});
	};

	const renderMatrixTabContent = () => {
		return (
			<div className="matrix-tab-wrapper">
				<div className="matrix-chart-column">
					<div className='p-2'>
						{
							chartConfig.data &&
							<Scatter
								data={chartConfig.data}
								options={chartConfig.options}
								plugins={[customLinesPlugin]}
							/>
						}
					</div>
					<div className="quadrant-legend">
						<div className="d-flex flex-wrap">
							{quadrantColors.map((item, index) => (
								<div key={index} className="legend-item me-4">
									<span
										className="legend-color"
										style={{ backgroundColor: item.color }}
									></span>
									<span className="legend-label ms-2">{item.label}</span>
								</div>
							))}
						</div>
					</div>
				</div>
				<div className="matrix-legend-column">
					<div className="matrix-number-legend">
						<div className="matrix-legend-title">{t('Matrix.label')}</div>
						<div className="matrix-legend-list">
							{scores?.sort((a, b) => a.stakeholderRanking - b.stakeholderRanking)?.map((score) => (
								<div key={score.questionId} className="matrix-legend-row">
									<span
										className="matrix-legend-dot"
										style={{ backgroundColor: colorMap[score?.topic?.category] || '#808080' }}
									>
										<span className="matrix-legend-num">{score.stakeholderRanking}</span>
									</span>
									<span className="matrix-legend-name">{score?.topic?.name}</span>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		);
	};

	const handleSort = (field) => {
		if (sortField === field && sortOrder === SORT_ORDER_DESC) {
			setSortOrder(SORT_ORDER_ASC);
		} else {
			setSortOrder(SORT_ORDER_DESC);
		}
		setSortField(field);
	};

	const setupScoreCardData = (response, sOrder, sField) => {
		const impactHolders = [];
		const financialHolders = [];
		const importanceHolders = [];
		let scoreData = [];
		const ids = [];
		response?.forEach((s, index) => {
			let finData = {};
			let impData = {};
			let importanceData = {};
			s.impacts?.forEach(imp => {
				let s = 0;
				imp.stakeholderGroups?.forEach(g => {
					s += g.weightedScore;
					if (imp.impactType === MaterialityImpactType.IMPACT) {
						if (impactHolders.findIndex(h => h.stakeholderGroupId === g.stakeholderGroupId) < 0) {
							impactHolders.push(g);
						}
					} else if (imp.impactType === MaterialityImpactType.IMPORTANCE) {
						if (importanceHolders.findIndex(h => h.stakeholderGroupId === g.stakeholderGroupId) < 0) {
							importanceHolders.push(g);
						}
					} else {
						if (financialHolders.findIndex(h => h.stakeholderGroupId === g.stakeholderGroupId) < 0) {
							financialHolders.push(g);
						}
					}
				});
				//const average = +((s / (imp.stakeholderGroups?.length ?? 1)).toFixed(2));
				const average = +((s).toFixed(2));
				if (imp.impactType === MaterialityImpactType.IMPACT) {
					impData = {
						averageScore: average,
						stakeholderGroups: imp.stakeholderGroups
					};
				} else if (imp.impactType === MaterialityImpactType.IMPORTANCE) {
					importanceData = {
						averageScore: average,
						stakeholderGroups: imp.stakeholderGroups
					};
				} else {
					finData = {
						averageScore: average,
						stakeholderGroups: imp.stakeholderGroups
					};
				}
			});
			const tAvg = s.topic?.materialityType === MaterialityType.SINGLE 
			?  +(((importanceData.averageScore + impData.averageScore) / 2).toFixed(2))
			:  +(((finData.averageScore + impData.averageScore) / 2).toFixed(2));
			scoreData.push({
				topic: s.topic,
				active: s.active,
				assessmentId: s.assessmentId,
				questionId: s.questionId,
				rankingByCompany: s.rankingByCompany,
				version: s.version,
				financial: finData,
				impact: impData,
				importance: importanceData,
				totalAverage: tAvg,
				stakeholderRanking: 0
			});
			if (s.active) {
				ids.push(s.questionId);
			}
		});
		setFinancialStakeholders(financialHolders);
		setImpactStakeholders(impactHolders);
		setImportanceStakeholders(importanceHolders);
		setMatIssueVersionType(scoreData[0]?.topic?.materialityType)
		applySorting(scoreData, sOrder, sField, scoreData[0]?.topic?.materialityType === MaterialityType.SINGLE);
		setScores(scoreData ?? []);
		setSelectedItems(ids);
		const sDataClone = JSON.parse(JSON.stringify(scoreData));
		setupChartData(sDataClone.sort((a, b) => a.stakeholderRanking - b.stakeholderRanking), scoreData[0]?.topic?.materialityType === MaterialityType.SINGLE);
	};

	const applySorting = (scoreData, sOrder, sField, isSingleMateriality) => {
		if (sField === SortBy.COMPANY_RANKING) {
			if (sOrder === SORT_ORDER_ASC) {
				scoreData = scoreData.sort((a, b) => {
					if (a.rankingByCompany === null || a.rankingByCompany === undefined) return 1;
					if (b.rankingByCompany === null || b.rankingByCompany === undefined) return -1;
					return a.rankingByCompany - b.rankingByCompany;
				});
			} else {
				scoreData = scoreData.sort((a, b) => {
					if (a.rankingByCompany === null || a.rankingByCompany === undefined) return 1;
					if (b.rankingByCompany === null || b.rankingByCompany === undefined) return -1;
					return b.rankingByCompany - a.rankingByCompany;
				});
			}
		} else if (sField === SortBy.FINANCIAL_WEIGHTED_SCORE) {
			if (sOrder === SORT_ORDER_ASC) {
				if (isSingleMateriality) {
					scoreData = scoreData.sort((a, b) => a.importance.averageScore - b.importance.averageScore);
				} else {
					scoreData = scoreData.sort((a, b) => a.financial.averageScore - b.financial.averageScore);
				}
			} else {
				if (isSingleMateriality) {
					scoreData = scoreData.sort((a, b) => b.importance.averageScore - a.importance.averageScore);
				} else {
					scoreData = scoreData.sort((a, b) => b.financial.averageScore - a.financial.averageScore);
				}
			}
		} else if (sField === SortBy.IMPACT_WEIGHTED_SCORE) {
			if (sOrder === SORT_ORDER_ASC) {
				scoreData = scoreData.sort((a, b) => a.impact.averageScore - b.impact.averageScore);
			} else {
				scoreData = scoreData.sort((a, b) => b.impact.averageScore - a.impact.averageScore);
			}
		} else {
			if (sOrder === SORT_ORDER_ASC) {
				scoreData = scoreData.sort((a, b) => a.totalAverage - b.totalAverage);
			} else {
				scoreData = scoreData.sort((a, b) => b.totalAverage - a.totalAverage);
			}
		}
		scoreData = assignStakeholderRanking(scoreData);
	};

	const assignStakeholderRanking = (data) => {
		const rankScore = (item) => {
			if (item.financial?.averageScore != null) {
				return item.totalAverage ?? 0;
			}
			const impactScore = item.impact?.averageScore ?? 0;
			const importanceScore = item.importance?.averageScore ?? 0;
			return (impactScore + importanceScore) / 2;
		};
		const sortedData = [...data].sort(
			(a, b) => rankScore(b) - rankScore(a)
		);
		data.forEach((item) => {
			item.stakeholderRanking = sortedData.findIndex((sortedItem) => sortedItem === item) + 1;
		});
		return data;
	}

	const renderFinAndImpactOriginalScores = (scoreData) => {
		let finScore = 0;
		if (scoreData.topic?.financialImpact) {
			finScore += MaterialityImpactRange[scoreData.topic?.financialImpact.range];
			finScore += MaterialityImpactRange[scoreData.topic?.financialImpact.likelihood];
		}
		finScore = +((finScore / 2).toFixed(2));
		let impScore = 0;
		if (matIssueVersionType === MaterialityType.SINGLE) {
			if (scoreData.topic?.importanceImpact?.scale !== null) {
				impScore += MaterialityImpactRange[scoreData.topic?.materialityImpact.scale];
			} else {
				impScore += MaterialityImpactRange[scoreData.topic?.materialityImpact.scope];
			}
			impScore = +(impScore.toFixed(2));
		} else {
			if (scoreData.topic?.materialityImpact) {
				impScore += MaterialityImpactRange[scoreData.topic?.materialityImpact.likelihood];
				impScore += MaterialityImpactRange[scoreData.topic?.materialityImpact.scale];
				impScore += MaterialityImpactRange[scoreData.topic?.materialityImpact.scope];
				impScore += MaterialityImpactRange[scoreData.topic?.materialityImpact.irremediability];
			}
			impScore = +((impScore / 4).toFixed(2));
		}
		let importanceScore = 0;
		if (matIssueVersionType === MaterialityType.SINGLE && scoreData.topic?.importanceImpact) {
			if (scoreData.topic?.importanceImpact?.scale !== null) {
				importanceScore += MaterialityImpactRange[scoreData.topic?.importanceImpact.scale];
			} else {
				importanceScore += MaterialityImpactRange[scoreData.topic?.importanceImpact.scope];
			}
		}
		importanceScore = +(importanceScore.toFixed(2));
		return (
			<>
				<td className='text-center LightBLue'>{impScore}</td>
				{ matIssueVersionType === MaterialityType.DOUBLE && <td className='text-center LightBLue'>{finScore}</td> }
				{ matIssueVersionType === MaterialityType.SINGLE && <td className='text-center LightBLue'>{importanceScore}</td> }
				<td className='text-center LightBLue'><span className='cursor ' onClick={() => setSelectedComment(scoreData)}><BiComment /></span></td>
			</>
		);
	};

	const finalizeScorecard = (graphImageURL = '') => {
		setLoaderDisplay(true);
		MaterialIssueService.finalizeScoreCardData(assessmentId, version, companyId, graphImageURL, companyName).then(res => {
			setLoaderDisplay(false);
			setSaveChanges(true);
			if (res.status !== 200) return;

			setShowProceedToReportConfirmation(true);
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	const renderQuadrantLegends = () => {
		return (
			<div className="quadrant-legend">
				<div className="d-flex flex-wrap">
					{quadrantColors.map((item, index) => (
						<div key={index} className="legend-item me-4">
							<span
								className="legend-color"
								style={{ backgroundColor: item.color }}
							></span>
							<span className="legend-label ms-2">{item.label}</span>
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderPlottedTopics = () => {
		return (
			<div className='matrixDt_Card'>
				{
					scores?.sort((a, b) => a.stakeholderRanking - b.stakeholderRanking)?.map(score => {
						const financialScore = score?.financial?.averageScore ?? 0;
						const impactScore = score?.impact?.averageScore ?? 0;
						const maxScore = Math.max(financialScore, impactScore);

						// let bgColor = '#808080'; // Default gray
						// if (maxScore >= 3) bgColor = '#FF0000';      // Red
						// else if (maxScore >= 2) bgColor = '#FFA500'; // Orange
						// else if (maxScore >= 1) bgColor = '#008000'; // Green
						// const category = score?.topic?.category ?? '';
						// const formattedCategory = category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
						return (
							<>
								{
									score.topic.category == 'ENVIRONMENTAL' && (
										<>
											<div className="matrixDt_CardCount ">
												<span className='environmentalimg' >{score?.stakeholderRanking}</span>
												<label>{score?.topic?.name} </label>
											</div>

										</>
									)
								}
								{
									score.topic.category == 'SOCIAL' && (
										<>
											<div className="matrixDt_CardCount ">
												<span className='Socialimg' >{score?.stakeholderRanking}</span>
												<label>{score?.topic?.name} </label>
											</div>
										</>
									)
								}
								{
									score.topic.category == 'GOVERNANCE' && (
										<>
											<div className="matrixDt_CardCount ">
												<span className='governanceimg' >{score?.stakeholderRanking}</span>
												<label>{score?.topic?.name} </label>
											</div>
										</>
									)
								}
							</>
						)
					})
				}
			</div>
		);
	};

	const renderMatrixTabContentForChartExport = () => {
		return (
			<div>
				<div>
					<div className='p-2'>
						{
							chartConfigrp.data &&
							<Scatter
								width={"400px"}
								height={"600px"}
								data={chartConfigrp.data}
								options={chartConfigrp.options} />
						}
					</div>
					{renderQuadrantLegends()}
				</div>
				<div>
					{renderPlottedTopics()}
				</div>
			</div>
		);
	};

	return (
		<div className='ScoreCardbox'>
			<div className='MaterialIssuesCard_tabCard'>
				{renderHeader()}
				<Tabs
					activeKey={activeTab}
					onSelect={(key) => setActiveTab(key)}
					id="uncontrolled-tab-example"
					className="mt-2">
					<Tab eventKey="scorecard" title={t('ScoreCard.label')}>
						<div className='ScoreCardbox_table mt-2 1'>
							<Table responsive hover>
								{renderTableHeader()}
								<tbody>
									{renderRows()}
								</tbody>
							</Table>
							<p style={{ color: '#0d6efd' }} className='p-2 pt-3'><b>{t('ScorecardScoresDetails.label')}</b></p>
							<div className='text-center p-2 pt-3'>
								{
									mode == 'EDIT' &&
									<Button variant='secondary' className='px-4 me-2' onClick={() => saveScorecard()}>{t('Save.label')}</Button>
								}
								{
									status == 'DRAFT' && mode == 'EDIT' &&
									<Button variant='primary' className='px-4' disabled={saveChanges} onClick={() => setShowConfirmation(true)}>{t('Finalize.label')}</Button>
								}
							</div>
						</div>
						{/* Note that we are hiding this div from UI for export by adjusting position,  otherwise exportRef wont be accessible with content */}
						<div ref={exportRef} className='invisible-position'>
							<div>{renderMatrixTabContentForChartExport()}</div>
						</div>
					</Tab>
					<Tab eventKey="matrix" title={t('Matrix.label')}>
						{renderMatrixTabContent()}
					</Tab>
				</Tabs>
			</div>

			{
				showConfirmation &&
				<ConfirmationModal
					type={'Scorecard'}
					modalShow={true}
					title={transformAssessmentName()}
					messageText={t("ScorecardFinalizePopupMessage.label")}
					callbackModal={confirmFinalize}
					confirmBtn={t("Confirm.label")}
					CancelBtn={t("Cancel.label")}
				/>
			}
			{
				showCloseConfirmation &&
				<ConfirmationModal
					type={'Scorecard'}
					modalShow={true}
					messageText={"Do you want to save the changes?"}
					callbackModal={saveChangesConfirmationCallBack}
					confirmBtn={t("Save")}
					CancelBtn={t("Don't Save")}
				/>
			}
			{
				selectedComment &&
				<MaterialityImpactSelectionModal
					validated={true}
					mode={'VIEW'}
					topicDetails={selectedComment.topic}
					financialImpact={selectedComment.topic.financialImpact}
					materialityImpact={selectedComment.topic.materialityImpact}
					importanceImpact={selectedComment.topic.importanceImpact}
					onClose={() => setSelectedComment(null)}
					materialityType={matIssueVersionType == 'SINGLE' ? 'Single' : 'Double'}
					show={true}
				/>
			}
			{
				selectedGroup &&
				<QuestionScoreModal
					title={selectedGroup.score?.topic?.name}
					companyId={companyId}
					questionId={selectedGroup.score.questionId}
					stakeholderGroupId={selectedGroup.groupId}
					matertialityType={selectedGroup.score?.topic?.materialityType}
					onClose={() => setSelectedGroup(null)} />
			}
			{
				showProceedToReportConfirmation &&
				<ConfirmationModal
					type={'Scorecard'}
					modalShow={true}
					title={t('FinalizedScoreCard.label')}
					messageText={t("ProceedToMatReport.label")}
					callbackModal={navigateToReport}
					confirmBtn={t("Proceed.label")}
					CancelBtn={t("Cancel.label")}
				/>
			}
			{
				showSaveModal &&
				<ConfirmationModal
					type={'Scorecard'}
					modalShow={true}
					messageText={t("ScorecardSavePopupMessage.label")}
					callbackModal={(val) => {
						if (val) {
							setShowConfirmation(true);
							setShowSaveModal(false);
						} else {
							setShowSaveModal(false);
						}
					}}
					confirmBtn={t("Finalize.label")}
					CancelBtn={t("Cancel.label")}
				/>
			}
		</div>
	)
};

export default withTranslation()(ScoreCard);